import { PaginationState, SortingState } from '@tanstack/react-table'
import { DOC_SEARCH_QUERY, useGeckoAnalytics } from 'features/analytics'
import {
  createAgenticConversationTurn,
  hasAgenticConversationEvents,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/agenticConversation'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import { useFavoriteSearches } from 'features/knowledgeDocumentViewer/hooks/useFavoriteSearches'
import {
  type DocSearchQueryPowerAnalyticsProperties,
  usePowerAnalyticsBackend,
} from 'features/knowledgeDocumentViewer/hooks/usePowerAnalyticsBackend'
import { useSearchQueryHistory } from 'features/knowledgeDocumentViewer/hooks/useSearchQueryHistory'
import {
  type AgenticSearchConversation,
  type DocumentSearchMode,
  type SearchHistoryItem,
  type SearchStateUpdate,
  type ViewMode,
} from 'features/knowledgeDocumentViewer/types'
import { FavoriteSearch } from 'features/knowledgeDocumentViewer/utils/favoriteSearchesStorage'
import { SearchQueryHistoryEntry } from 'features/knowledgeDocumentViewer/utils/searchQueryHistoryStorage'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  DocumentsViewerContentFilter,
  urlSearchParamsFromColumnFilters,
} from 'routes/documents/-searchParams'

export interface SearchSubmitOptions {
  clearInputAfterSubmit?: boolean
  startAgenticTurn?: boolean
}

export interface SearchSubmittedEvent {
  query: string
  options?: SearchSubmitOptions
  previousSearchState: SearchHistoryItem['searchState']
  isLinkedDocsMode: boolean
}

type SearchInputChangeEvent = React.ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement
>

function getAgenticConversation(
  searchState: SearchHistoryItem['searchState'],
  siteId: string
): AgenticSearchConversation | undefined {
  if (searchState.agenticSearch?.siteId !== siteId) return undefined
  const conversation = searchState.agenticSearch.conversation
  if (!conversation.turns.every(hasAgenticConversationEvents)) return undefined
  return conversation
}

function buildAgenticSearchState({
  currentSearchState,
  siteId,
  query,
  turnId,
}: {
  currentSearchState: SearchHistoryItem['searchState']
  siteId: string
  query: string
  turnId: string
}): NonNullable<SearchHistoryItem['searchState']['agenticSearch']> {
  const currentConversation = getAgenticConversation(currentSearchState, siteId)
  const nextTurn = createAgenticConversationTurn({ turnId, query })
  const nextConversation: AgenticSearchConversation = {
    activeTurnId: turnId,
    turns: [...(currentConversation?.turns ?? []), nextTurn],
  }
  if (currentConversation?.id) {
    nextConversation.id = currentConversation.id
  }
  return {
    siteId,
    conversation: nextConversation,
  }
}

interface AdvancedSearchUXContextType {
  siteId: string
  searchState: SearchHistoryItem['searchState']
  /** When true, show only documents list (no search bar, no Documents/Sensors tabs). */
  isLinkedDocsMode: boolean
  /** Whether this search tab is currently visible and accepting keyboard input. */
  isActive: boolean

  // Core search controls
  searchInput: string
  setSearchInput: React.Dispatch<React.SetStateAction<string>>
  contentFilter: DocumentsViewerContentFilter
  searchMode: DocumentSearchMode
  viewMode: ViewMode

  // Derived from searchState for convenience
  selectedDocumentId: string | null
  pagination: PaginationState
  sorting: SortingState
  columnFilters: SearchHistoryItem['searchState']['filters']

  // Shared handlers for search + tabs + view mode
  onSearchStateChange: (updates: SearchStateUpdate) => void
  handleSearchChange: (e: SearchInputChangeEvent) => void
  handleSearchSubmit: (options?: SearchSubmitOptions) => void
  handleSearchKeyDown: (e: React.KeyboardEvent) => void
  handleTabChange: (tab: DocumentsViewerContentFilter) => void
  handleSearchModeChange: (mode: DocumentSearchMode) => void
  handleViewModeChange: (
    value: string | number,
    options?: { selectedDocumentIdWhenSwitchingToTable?: string | null }
  ) => void

  // Table & selection helpers (search-state aware)
  setPagination: (
    updater: PaginationState | ((prev: PaginationState) => PaginationState)
  ) => void
  setSorting: (
    updater: SortingState | ((prev: SortingState) => SortingState)
  ) => void
  clearSelectedDocument: () => void
  setSelectedDocumentId: (id: string) => void

  // Layout & display preferences
  leftPaneWidthPercent: number
  setLeftPaneWidthPercent: React.Dispatch<React.SetStateAction<number>>
  showIndividualScores: boolean
  setShowIndividualScores: React.Dispatch<React.SetStateAction<boolean>>

  // Search query history
  searchSuggestions: SearchQueryHistoryEntry[]
  onSuggestionSelect: (query: string, options?: SearchSubmitOptions) => void
  onClearSearchHistory: () => void
  favoriteSearches: FavoriteSearch[]
  isSearchFavorited: (query: string) => boolean
  toggleSearchFavorite: (query: string, contentFilter: string) => void
}

interface AdvancedSearchUXProviderProps {
  children: React.ReactNode
  siteId: string
  searchState: SearchHistoryItem['searchState']
  isLinkedDocsMode?: boolean
  isActive?: boolean
  tabId?: string
  onSearchSubmitted: (event: SearchSubmittedEvent) => void
}

const AdvancedSearchUXContext = createContext<
  AdvancedSearchUXContextType | undefined
>(undefined)

const DOCUMENT_SEARCH_MODE_STORAGE_KEY = 'documentSearch_searchMode:v1'

function getStoredDocumentSearchMode(
  fallback: DocumentSearchMode | undefined
): DocumentSearchMode {
  try {
    const stored = localStorage.getItem(DOCUMENT_SEARCH_MODE_STORAGE_KEY)
    if (stored === 'normal' || stored === 'agentic') return stored
  } catch {
    /* fallback to current tab state */
  }
  return fallback ?? 'agentic'
}

export const AdvancedSearchUXProvider: React.FC<
  AdvancedSearchUXProviderProps
> = ({
  children,
  siteId,
  searchState,
  isLinkedDocsMode = false,
  isActive = true,
  tabId,
  onSearchSubmitted,
}) => {
  const { onSearchStateChange: onSearchStateChangeForActiveTab } =
    useSearchActions()
  const { trackEvent } = useGeckoAnalytics()
  const { sendPowerAnalyticsEvent } = usePowerAnalyticsBackend(siteId)
  const { addQuery, getFilteredQueries, recentQueries, clearHistory } =
    useSearchQueryHistory(siteId)
  const { favoriteSearches, isSearchFavorited, toggleSearchFavorite } =
    useFavoriteSearches(siteId)

  const onSearchStateChange = useCallback(
    (updates: SearchStateUpdate) => {
      if (tabId) {
        onSearchStateChangeForActiveTab(updates, { tabId })
        return
      }
      onSearchStateChangeForActiveTab(updates)
    },
    [onSearchStateChangeForActiveTab, tabId]
  )

  const [searchInput, setSearchInput] = useState(
    () => searchState.inputQuery ?? searchState.query
  )
  const [searchModePreference, setSearchModePreference] =
    useState<DocumentSearchMode>(() =>
      getStoredDocumentSearchMode(searchState.searchMode)
    )
  const hasSubmittedSearch =
    isLinkedDocsMode ||
    searchState.query.trim().length > 0 ||
    searchState.browseAll === true
  const searchMode = hasSubmittedSearch
    ? searchState.searchMode
    : searchModePreference
  const [leftPaneWidthPercent, setLeftPaneWidthPercent] = useState(() => {
    try {
      const stored = localStorage.getItem('documentSearch_previewPanelWidth:v1')
      if (stored) {
        const parsed = Number(stored)
        if (!Number.isNaN(parsed) && parsed >= 20 && parsed <= 80) return parsed
      }
    } catch {
      /* fallback to default */
    }
    return 70
  })
  const [showIndividualScores, setShowIndividualScores] = useState(() => {
    try {
      const stored = localStorage.getItem(
        'documentSearch_showIndividualScores:v1'
      )
      if (stored !== null) return stored === 'true'
    } catch {
      /* fallback to default */
    }
    return true
  })

  // Keep local input in sync when external state changes (e.g. URL/tab restore)
  useEffect(() => {
    setSearchInput(searchState.inputQuery ?? searchState.query)
  }, [searchState.inputQuery, searchState.query])

  useEffect(() => {
    try {
      localStorage.setItem(
        'documentSearch_showIndividualScores:v1',
        String(showIndividualScores)
      )
    } catch {
      /* localStorage unavailable */
    }
  }, [showIndividualScores])

  useEffect(() => {
    try {
      localStorage.setItem(
        'documentSearch_previewPanelWidth:v1',
        String(leftPaneWidthPercent)
      )
    } catch {
      /* localStorage unavailable */
    }
  }, [leftPaneWidthPercent])

  useEffect(() => {
    try {
      localStorage.setItem(
        DOCUMENT_SEARCH_MODE_STORAGE_KEY,
        searchModePreference
      )
    } catch {
      /* localStorage unavailable */
    }
  }, [searchModePreference])

  const handleSearchChange = useCallback(
    (e: SearchInputChangeEvent) => {
      const inputQuery = e.target.value
      setSearchInput(inputQuery)
      onSearchStateChange({ inputQuery })
    },
    [onSearchStateChange]
  )

  const handleSearchSubmit = useCallback(
    (options?: SearchSubmitOptions) => {
      const q = searchInput.trim()
      const isBrowseAll = !q
      const shouldStartAgenticTurn =
        searchMode === 'agentic' && searchState.contentFilter === 'documents'
      const shouldClearInputAfterSubmit =
        options?.clearInputAfterSubmit ?? shouldStartAgenticTurn
      const turnId =
        shouldStartAgenticTurn && q
          ? `agentic-turn-${crypto.randomUUID()}`
          : undefined
      const queryPayload: DocSearchQueryPowerAnalyticsProperties = {
        query: q || '(browse all)',
        browse_all: isBrowseAll,
        content_filter: searchState.contentFilter,
        site_id: siteId,
        search_mode: shouldStartAgenticTurn ? 'agentic' : 'normal',
        ...urlSearchParamsFromColumnFilters(searchState.filters ?? []),
      }
      trackEvent(DOC_SEARCH_QUERY.title, queryPayload)
      sendPowerAnalyticsEvent(DOC_SEARCH_QUERY.title, queryPayload)
      if (q) {
        addQuery(q, searchState.contentFilter)
      }
      const nextSearchInput = shouldClearInputAfterSubmit ? '' : q
      const inputQuery = shouldClearInputAfterSubmit ? '' : undefined
      onSearchStateChange((currentSearchState) => ({
        query: q,
        inputQuery,
        searchMode:
          currentSearchState.contentFilter === 'documents'
            ? searchMode
            : 'normal',
        browseAll: isBrowseAll,
        agenticSearch: turnId
          ? buildAgenticSearchState({
              currentSearchState,
              siteId,
              query: q,
              turnId,
            })
          : undefined,
        pagination: {
          pageIndex: 0,
          pageSize: currentSearchState.pagination.pageSize,
        },
        selectedDocumentId: null,
      }))
      setSearchInput(nextSearchInput)
      onSearchSubmitted({
        query: q,
        options: {
          ...options,
          startAgenticTurn: shouldStartAgenticTurn,
        },
        previousSearchState: searchState,
        isLinkedDocsMode,
      })
    },
    [
      onSearchStateChange,
      searchInput,
      searchState,
      trackEvent,
      sendPowerAnalyticsEvent,
      siteId,
      addQuery,
      onSearchSubmitted,
      isLinkedDocsMode,
      searchMode,
    ]
  )

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.stopPropagation()
        handleSearchSubmit()
      }
    },
    [handleSearchSubmit]
  )

  const handleTabChange = useCallback(
    (tab: DocumentsViewerContentFilter) => {
      onSearchStateChange({
        contentFilter: tab,
        // Clear selection when switching tabs
        selectedDocumentId: null,
        inputQuery: '',
        agenticSearch: undefined,
      })
      setSearchInput('')
    },
    [onSearchStateChange]
  )

  const handleSearchModeChange = useCallback((mode: DocumentSearchMode) => {
    setSearchModePreference(mode)
  }, [])

  const handleViewModeChange = useCallback(
    (
      value: string | number,
      options?: { selectedDocumentIdWhenSwitchingToTable?: string | null }
    ) => {
      if (
        value === 'table' &&
        options?.selectedDocumentIdWhenSwitchingToTable !== undefined
      ) {
        onSearchStateChange({
          viewMode: 'table',
          selectedDocumentId:
            options.selectedDocumentIdWhenSwitchingToTable ?? null,
        })
      } else {
        onSearchStateChange({ viewMode: value as ViewMode })
      }
    },
    [onSearchStateChange]
  )

  const setPagination = useCallback(
    (
      updater: PaginationState | ((prev: PaginationState) => PaginationState)
    ) => {
      const next =
        typeof updater === 'function'
          ? updater(searchState.pagination)
          : updater
      onSearchStateChange({ pagination: next })
    },
    [onSearchStateChange, searchState.pagination]
  )

  const setSorting = useCallback(
    (updater: SortingState | ((prev: SortingState) => SortingState)) => {
      const next =
        typeof updater === 'function' ? updater(searchState.sorting) : updater
      onSearchStateChange({ sorting: next })
    },
    [onSearchStateChange, searchState.sorting]
  )

  const clearSelectedDocument = useCallback(() => {
    onSearchStateChange({ selectedDocumentId: null })
  }, [onSearchStateChange])

  const setSelectedDocumentId = useCallback(
    (id: string) => {
      onSearchStateChange({ selectedDocumentId: id })
    },
    [onSearchStateChange]
  )

  const searchSuggestions = useMemo(() => {
    const trimmed = searchInput.trim()
    if (trimmed) {
      return getFilteredQueries(trimmed)
    }
    return recentQueries()
  }, [searchInput, getFilteredQueries, recentQueries])

  const onSuggestionSelect = useCallback(
    (query: string, options?: SearchSubmitOptions) => {
      const trimmed_query = query.trim()
      const shouldStartAgenticTurn =
        searchMode === 'agentic' && searchState.contentFilter === 'documents'
      const shouldClearInputAfterSubmit =
        options?.clearInputAfterSubmit ?? shouldStartAgenticTurn
      const turnId =
        shouldStartAgenticTurn && trimmed_query
          ? `agentic-turn-${crypto.randomUUID()}`
          : undefined
      const nextSearchInput = shouldClearInputAfterSubmit ? '' : trimmed_query
      const inputQuery = shouldClearInputAfterSubmit ? '' : undefined
      setSearchInput(nextSearchInput)
      onSearchStateChange((currentSearchState) => ({
        query: trimmed_query,
        inputQuery,
        searchMode:
          currentSearchState.contentFilter === 'documents'
            ? searchMode
            : 'normal',
        browseAll: false,
        agenticSearch: turnId
          ? buildAgenticSearchState({
              currentSearchState,
              siteId,
              query: trimmed_query,
              turnId,
            })
          : undefined,
        pagination: {
          pageIndex: 0,
          pageSize: currentSearchState.pagination.pageSize,
        },
        selectedDocumentId: null,
      }))
      addQuery(trimmed_query, searchState.contentFilter)
      onSearchSubmitted({
        query: trimmed_query,
        options: {
          ...options,
          startAgenticTurn: shouldStartAgenticTurn,
        },
        previousSearchState: searchState,
        isLinkedDocsMode,
      })
    },
    [
      setSearchInput,
      onSearchStateChange,
      searchState,
      siteId,
      addQuery,
      onSearchSubmitted,
      isLinkedDocsMode,
      searchMode,
    ]
  )

  const onClearSearchHistory = useCallback(() => {
    clearHistory()
  }, [clearHistory])

  const contextValue = useMemo<AdvancedSearchUXContextType>(
    () => ({
      siteId,
      searchState,
      isLinkedDocsMode,
      isActive,
      searchInput,
      setSearchInput,
      contentFilter: searchState.contentFilter,
      searchMode,
      viewMode: searchState.viewMode,
      selectedDocumentId: searchState.selectedDocumentId ?? null,
      pagination: searchState.pagination,
      sorting: searchState.sorting,
      columnFilters: searchState.filters,
      onSearchStateChange,
      handleSearchChange,
      handleSearchSubmit,
      handleSearchKeyDown,
      handleTabChange,
      handleSearchModeChange,
      handleViewModeChange,
      setPagination,
      setSorting,
      clearSelectedDocument,
      setSelectedDocumentId,
      leftPaneWidthPercent,
      setLeftPaneWidthPercent,
      showIndividualScores,
      setShowIndividualScores,
      searchSuggestions,
      onSuggestionSelect,
      onClearSearchHistory,
      favoriteSearches,
      isSearchFavorited,
      toggleSearchFavorite,
    }),
    [
      siteId,
      searchState,
      searchMode,
      isLinkedDocsMode,
      isActive,
      searchInput,
      onSearchStateChange,
      handleSearchChange,
      handleSearchSubmit,
      handleSearchKeyDown,
      handleTabChange,
      handleSearchModeChange,
      handleViewModeChange,
      setPagination,
      setSorting,
      clearSelectedDocument,
      setSelectedDocumentId,
      leftPaneWidthPercent,
      setLeftPaneWidthPercent,
      showIndividualScores,
      setShowIndividualScores,
      searchSuggestions,
      onSuggestionSelect,
      onClearSearchHistory,
      favoriteSearches,
      isSearchFavorited,
      toggleSearchFavorite,
    ]
  )

  return (
    <AdvancedSearchUXContext.Provider value={contextValue}>
      {children}
    </AdvancedSearchUXContext.Provider>
  )
}

export const useAdvancedSearchUX = (): AdvancedSearchUXContextType => {
  const ctx = useContext(AdvancedSearchUXContext)
  if (!ctx) {
    throw new Error(
      'useAdvancedSearchUX must be used within an AdvancedSearchUXProvider'
    )
  }
  return ctx
}
